/// <reference types="vite/client" />
import { dedupeGet, eaCaches, readTtl, writeTtl } from '../utils/sharedTtlCache';

const API_BASE =
  import.meta.env.VITE_API_URL ||
  import.meta.env.VITE_BACKEND_URL ||
  'http://localhost:8080';

const SCORING_TTL_MS = 60_000;

export interface ScoringRationaleApiV2Data {
  header?: {
    overallScore?: number;
    rankBadge?: number;
    priorityBand?: string;
    quadrant?: string;
    businessValueScore?: number;
    easeOfImplementationScore?: number;
    useCaseName?: string | null;
    useCaseCategory?: string | null;
    use_case_name?: string | null;
    use_case_category?: string | null;
  };
  scoring_dimensions?: ScoringRationaleDimensionRaw[];
}

export interface ScoringRationaleDimensionRaw {
  category_id: number;
  category_name: string;
  description?: string;
  dimension_type?: string;
  dimension_score?: number | null;
  category_weight?: number | null;
  category_weight_label?: string | null;
  contributing_factor_count?: number;
  contributing_factors?: Array<{
    measure_id?: number;
    measure_name?: string;
    description?: string;
    score?: number | null;
    weight?: number | null;
    weight_label?: string | null;
    rationalization?: string;
    source_type?: string;
  }>;
}

export interface ScoringRationaleResponse {
  success: boolean;
  data?: ScoringRationaleApiV2Data;
  generated_at?: string;
  use_case_name?: string;
  use_case_category?: string;
  overall_score?: number | null;
  scores?: { rank?: number; total_score?: number };
  scoring_dimensions?: ScoringRationaleDimensionRaw[];
  meta?: { dimension_count?: number };
  error?: string;
}

function scoringUrl(clientId: string, useCaseId: string): string {
  return `${API_BASE}/api/v2/scoring-rationale/${clientId}/${useCaseId}`;
}

export function peekScoringRationale(
  clientId: string,
  useCaseId: string,
): ScoringRationaleResponse | null {
  return readTtl(eaCaches().scoring, scoringUrl(clientId, useCaseId), SCORING_TTL_MS);
}

export async function fetchScoringRationale(
  clientId: string,
  useCaseId: string,
): Promise<ScoringRationaleResponse> {
  const url = scoringUrl(clientId, useCaseId);
  const cached = peekScoringRationale(clientId, useCaseId);
  if (cached) return cached;

  return dedupeGet(url, async () => {
    const res = await fetch(url, { credentials: 'include' });
    const body = (await res.json()) as ScoringRationaleResponse;
    if (!res.ok || !body?.success) {
      throw new Error(body?.error || `Scoring rationale API ${res.status}`);
    }
    writeTtl(eaCaches().scoring, url, body);
    return body;
  });
}
